import React, { useEffect, useState } from 'react';
import type {
    DashboardFeature,
    DashboardFeatureAccessByRole,
    SelfServicePlanCode,
} from '../interfaces/Frameworks';
import {
    EditablePlanCatalogVersion,
    getPlanCatalogAdminState,
    getPlanCatalogPreview,
    getSaasErrorMessage,
    PlanCatalogAdminVersion,
    PublicPlan,
    publishPlanCatalogVersion,
    savePlanCatalogDraft,
    updatePlanSpeechNeeds,
    validatePlanCatalogVersion,
} from './SaasApi';
import './PlanCatalogAdmin.css';

const planCodes: SelfServicePlanCode[] = ['starter', 'premium'];
const coreFeatures: DashboardFeature[] = ['clients', 'organisation_team', 'organisation_team_manage'];
const catalogFeatures: Array<{ id: DashboardFeature; label: string; help: string }> = [
    { id: 'clients', label: 'Clients', help: 'Client management and progress.' },
    { id: 'audio_scribe_reports', label: 'Audio Scribe and AI reports', help: 'Recording, transcription, notes and AI reports.' },
    { id: 'messaging', label: 'Messaging campaigns', help: 'SMS and email campaigns.' },
    { id: 'organisation_analytics_heatmap', label: 'Organisation analytics', help: 'Existing embedded Amplitude dashboard.' },
    { id: 'organisation_team', label: 'View team', help: 'View dashboard members.' },
    { id: 'organisation_team_manage', label: 'Manage team', help: 'Create and manage dashboard members.' },
];

const planLabels: Record<SelfServicePlanCode, string> = {
    starter: 'Free trial',
    premium: 'Premium',
};

const todayVersionId = (planCode: SelfServicePlanCode): string => {
    const now = new Date();
    const parts = [
        now.getUTCFullYear(),
        String(now.getUTCMonth() + 1).padStart(2, '0'),
        String(now.getUTCDate()).padStart(2, '0'),
        String(now.getUTCHours()).padStart(2, '0'),
        String(now.getUTCMinutes()).padStart(2, '0'),
        String(now.getUTCSeconds()).padStart(2, '0'),
    ];
    return `${planCode}-${parts.join('')}-draft`;
};

const defaultAccess = (features: DashboardFeature[]): DashboardFeatureAccessByRole => {
    const allowed = new Set(features);
    return {
        admin: Object.fromEntries(catalogFeatures.map(({ id }) => [id, allowed.has(id)])),
        healthcare_professional: Object.fromEntries(catalogFeatures.map(({ id }) => [
            id,
            allowed.has(id) && !['organisation_team', 'organisation_team_manage'].includes(id),
        ])),
    };
};

const createPlanTemplate = (planCode: SelfServicePlanCode): EditablePlanCatalogVersion => {
    // Start from the agreed tier shape; every value remains editable before remote publication.
    const features = planCode === 'starter' ?
        [...coreFeatures, 'audio_scribe_reports' as DashboardFeature] :
        [
            ...coreFeatures,
            'audio_scribe_reports',
            'messaging',
            'organisation_analytics_heatmap',
        ] as DashboardFeature[];
    return {
        planCode,
        versionId: todayVersionId(planCode),
        name: planLabels[planCode],
        description: planCode === 'starter' ?
            'Try SuperPenguin with one dashboard seat and five family licences.' :
            'Everything included, sized to your practice.',
        publiclyPurchasable: true,
        displayOrder: planCodes.indexOf(planCode) + 1,
        highlighted: planCode === 'premium',
        currency: 'GBP',
        monthlyDisplayPrice: planCode === 'starter' ? 0 : undefined,
        annualDisplayPrice: planCode === 'starter' ? 0 : undefined,
        pricesIncludeTax: false,
        dashboardSeatsPerUnit: 1,
        familyLicensesPerUnit: planCode === 'starter' ? 5 : 50,
        minimumUnits: 1,
        maximumSelfServeUnits: planCode === 'starter' ? 1 : 20,
        usageLimits: {
            audioScribeRecordings: planCode === 'starter' ? 5 : null,
            aiGeneratedReports: planCode === 'starter' ? 5 : null,
        },
        allowedDashboardFeatures: features,
        defaultAccessByRole: defaultAccess(features),
    };
};

const copyPublishedPlan = (plan: PublicPlan): EditablePlanCatalogVersion => ({
    ...plan,
    // A new draft needs its own immutable version identifier and fresh Stripe identifiers.
    versionId: todayVersionId(plan.planCode),
    monthlyStripePriceId: undefined,
    annualStripePriceId: undefined,
    defaultAccessByRole: JSON.parse(JSON.stringify(plan.defaultAccessByRole)),
    allowedDashboardFeatures: [...plan.allowedDashboardFeatures],
});

const reopenDraft = (version: PlanCatalogAdminVersion): EditablePlanCatalogVersion => ({
    planCode: version.planCode,
    versionId: version.versionId,
    name: version.name,
    description: version.description,
    publiclyPurchasable: version.publiclyPurchasable,
    displayOrder: version.displayOrder,
    highlighted: version.highlighted,
    currency: version.currency,
    monthlyDisplayPrice: version.monthlyDisplayPrice,
    annualDisplayPrice: version.annualDisplayPrice,
    monthlyStripePriceId: version.monthlyStripePriceId,
    annualStripePriceId: version.annualStripePriceId,
    pricesIncludeTax: false,
    dashboardSeatsPerUnit: version.dashboardSeatsPerUnit,
    familyLicensesPerUnit: version.familyLicensesPerUnit,
    minimumUnits: version.minimumUnits,
    maximumSelfServeUnits: version.maximumSelfServeUnits,
    usageLimits: { ...version.usageLimits },
    allowedDashboardFeatures: [...version.allowedDashboardFeatures],
    defaultAccessByRole: JSON.parse(JSON.stringify(version.defaultAccessByRole)),
});

const latestSavedDraft = (versions: PlanCatalogAdminVersion[]): PlanCatalogAdminVersion | undefined =>
    versions
        .filter((version) => version.status === 'draft')
        .sort((left, right) => {
            const leftUpdatedAt = Date.parse(left.updatedAt || '') || 0;
            const rightUpdatedAt = Date.parse(right.updatedAt || '') || 0;
            return rightUpdatedAt - leftUpdatedAt;
        })[0];

const cloneDraft = (draft: EditablePlanCatalogVersion): EditablePlanCatalogVersion =>
    JSON.parse(JSON.stringify(draft));

const numberOrUndefined = (value: string): number | undefined =>
    value.trim() === '' ? undefined : Number(value);

const formatGbp = (value: number | undefined): string =>
    value === undefined ? 'Not configured' : new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
    }).format(value);

const assertPublicationReady = (draft: EditablePlanCatalogVersion): void => {
    if (!draft.publiclyPurchasable) {
        throw new Error('Enable “Publicly purchasable” before Stripe validation and publication.');
    }
};

type ActionState = 'idle' | 'loading' | 'saving' | 'validating' | 'previewing' | 'publishing';

export const PlanCatalogAdmin: React.FC = () => {
    const [selectedPlanCode, setSelectedPlanCode] = useState<SelfServicePlanCode>('starter');
    const [drafts, setDrafts] = useState<Record<SelfServicePlanCode, EditablePlanCatalogVersion>>(() => ({
        starter: createPlanTemplate('starter'),
        premium: createPlanTemplate('premium'),
    }));
    const [publishedVersionIds, setPublishedVersionIds] = useState<Partial<Record<SelfServicePlanCode, string>>>({});
    const [savedVersions, setSavedVersions] = useState<Partial<Record<SelfServicePlanCode, string>>>({});
    const [stripeValidatedVersions, setStripeValidatedVersions] = useState<Partial<Record<SelfServicePlanCode, string>>>({});
    const [verifyStripe, setVerifyStripe] = useState(true);
    const [previewPlans, setPreviewPlans] = useState<PublicPlan[] | null>(null);
    const [actionState, setActionState] = useState<ActionState>('loading');
    const [catalogStateLoaded, setCatalogStateLoaded] = useState(false);
    const [speechNeedOptions, setSpeechNeedOptions] = useState<Array<{ id: string; name: string }>>([]);
    const [planSpeechNeeds, setPlanSpeechNeeds] = useState<Record<SelfServicePlanCode, string[] | null>>({
        starter: null,
        premium: null,
    });
    const [savedPlanSpeechNeeds, setSavedPlanSpeechNeeds] = useState<Record<SelfServicePlanCode, string[] | null>>({
        starter: null,
        premium: null,
    });
    const [incompatibleVersionIds, setIncompatibleVersionIds] = useState<Record<SelfServicePlanCode, string[]>>({
        starter: [],
        premium: [],
    });
    const [isUpdatingSpeechNeeds, setIsUpdatingSpeechNeeds] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const currentDraft = drafts[selectedPlanCode];
    const currentSaved = savedVersions[selectedPlanCode] === currentDraft.versionId;
    const currentStripeValidated = stripeValidatedVersions[selectedPlanCode] === currentDraft.versionId;

    const loadAdminState = async (replaceDrafts: boolean) => {
        setActionState('loading');
        setError(null);
        setMessage(null);
        try {
            const catalogState = await getPlanCatalogAdminState();
            setSpeechNeedOptions(catalogState.speechNeedOptions);
            const nextPlanSpeechNeeds = {} as Record<SelfServicePlanCode, string[] | null>;
            const nextIncompatibleVersionIds = {} as Record<SelfServicePlanCode, string[]>;
            catalogState.plans.forEach((planState) => {
                nextPlanSpeechNeeds[planState.planCode] = planState.speechNeeds === null ?
                    null : [...planState.speechNeeds];
                nextIncompatibleVersionIds[planState.planCode] = Array.isArray(planState.incompatibleVersionIds) ?
                    [...planState.incompatibleVersionIds] : [];
            });
            setPlanSpeechNeeds(nextPlanSpeechNeeds);
            setSavedPlanSpeechNeeds(nextPlanSpeechNeeds);
            setIncompatibleVersionIds(nextIncompatibleVersionIds);
            const activeVersions: Partial<Record<SelfServicePlanCode, string>> = {};
            catalogState.plans.forEach((planState) => {
                if (planState.activeVersionId) activeVersions[planState.planCode] = planState.activeVersionId;
            });
            setPublishedVersionIds(activeVersions);
            setCatalogStateLoaded(true);
            if (replaceDrafts) {
                const nextDrafts = {} as Record<SelfServicePlanCode, EditablePlanCatalogVersion>;
                const reopenedVersions: Partial<Record<SelfServicePlanCode, string>> = {};
                const reopenedLabels: string[] = [];
                catalogState.plans.forEach((planState) => {
                    const savedDraft = latestSavedDraft(planState.versions);
                    const activeVersion = planState.versions.find(
                        (version) => version.versionId === planState.activeVersionId
                    );
                    if (savedDraft) {
                        nextDrafts[planState.planCode] = reopenDraft(savedDraft);
                        reopenedVersions[planState.planCode] = savedDraft.versionId;
                        reopenedLabels.push(`${planLabels[planState.planCode]} ${savedDraft.versionId}`);
                    } else {
                        nextDrafts[planState.planCode] = activeVersion ?
                            copyPublishedPlan(activeVersion) : createPlanTemplate(planState.planCode);
                    }
                });
                setDrafts(nextDrafts);
                setSavedVersions(reopenedVersions);
                setStripeValidatedVersions({});
                setPreviewPlans(null);
                setMessage(reopenedLabels.length > 0 ?
                    `Reopened saved draft${reopenedLabels.length === 1 ? '' : 's'}: ${reopenedLabels.join(', ')}.` :
                    null);
            }
        } catch (loadError: unknown) {
            setError(`Catalogue drafts could not be loaded: ${getSaasErrorMessage(loadError)}`);
        } finally {
            setActionState('idle');
        }
    };

    useEffect(() => {
        void loadAdminState(true);
        // Load only when the Root-only administration surface mounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateDraft = (change: (draft: EditablePlanCatalogVersion) => void) => {
        setDrafts((current) => {
            const nextDraft = cloneDraft(current[selectedPlanCode]);
            change(nextDraft);
            return { ...current, [selectedPlanCode]: nextDraft };
        });
        setSavedVersions((current) => ({ ...current, [selectedPlanCode]: undefined }));
        setStripeValidatedVersions((current) => ({ ...current, [selectedPlanCode]: undefined }));
        setPreviewPlans(null);
        setMessage(null);
        setError(null);
    };

    const setAllowedFeature = (feature: DashboardFeature, enabled: boolean) => updateDraft((draft) => {
        const allowed = new Set(draft.allowedDashboardFeatures);
        if (enabled) allowed.add(feature); else allowed.delete(feature);
        draft.allowedDashboardFeatures = catalogFeatures
            .map(({ id }) => id)
            .filter((candidate) => allowed.has(candidate));
        for (const role of ['admin', 'healthcare_professional']) {
            draft.defaultAccessByRole[role] ||= {};
            if (!enabled) draft.defaultAccessByRole[role][feature] = false;
        }
    });

    const setRoleFeature = (role: 'admin' | 'healthcare_professional', feature: DashboardFeature, enabled: boolean) =>
        updateDraft((draft) => {
            draft.defaultAccessByRole[role] ||= {};
            draft.defaultAccessByRole[role][feature] = enabled;
        });

    const setSpeechNeed = (speechNeedId: string, enabled: boolean) => {
        const selectedIds = new Set(planSpeechNeeds[selectedPlanCode] || []);
        if (enabled) selectedIds.add(speechNeedId); else selectedIds.delete(speechNeedId);
        const knownIds = speechNeedOptions.map((speechNeed) => speechNeed.id);
        const unknownIds = Array.from(selectedIds).filter((id) => !knownIds.includes(id));
        setPlanSpeechNeeds((current) => ({
            ...current,
            [selectedPlanCode]: [...knownIds.filter((id) => selectedIds.has(id)), ...unknownIds],
        }));
        setMessage(null);
        setError(null);
    };

    const applyPlanSpeechNeeds = async () => {
        const speechNeeds = planSpeechNeeds[selectedPlanCode];
        if (!speechNeeds || speechNeeds.length === 0) {
            setError('Select at least one completed, safe speech need before applying this tier allowlist.');
            return;
        }
        if (!window.confirm(
            `Apply speech-needs access to the ${planLabels[selectedPlanCode]} tier? ` +
            'This immediately updates every current subscriber on this tier. Removed speech needs stop being available for new assignment and activation-key redemption.'
        )) return;
        setIsUpdatingSpeechNeeds(true);
        setMessage(null);
        setError(null);
        try {
            const result = await updatePlanSpeechNeeds(selectedPlanCode, speechNeeds);
            setPlanSpeechNeeds((current) => ({ ...current, [selectedPlanCode]: [...result.speechNeeds] }));
            setSavedPlanSpeechNeeds((current) => ({ ...current, [selectedPlanCode]: [...result.speechNeeds] }));
            setMessage(
                `${planLabels[selectedPlanCode]} speech-needs access was updated for ${result.updatedProviders} current subscriber` +
                `${result.updatedProviders === 1 ? '' : 's'}.`
            );
        } catch (updateError: unknown) {
            setError(getSaasErrorMessage(updateError));
        } finally {
            setIsUpdatingSpeechNeeds(false);
        }
    };

    const saveCurrentDraft = async (): Promise<string> => {
        const draft = drafts[selectedPlanCode];
        if (!draft.versionId.trim()) throw new Error('Enter a plan version ID before saving.');
        const result = await savePlanCatalogDraft(selectedPlanCode, draft.versionId.trim(), draft);
        if (result.versionId !== draft.versionId) {
            setDrafts((current) => ({
                ...current,
                [selectedPlanCode]: { ...current[selectedPlanCode], versionId: result.versionId },
            }));
        }
        setSavedVersions((current) => ({ ...current, [selectedPlanCode]: result.versionId }));
        setStripeValidatedVersions((current) => ({ ...current, [selectedPlanCode]: undefined }));
        return result.versionId;
    };

    const save = async () => {
        setActionState('saving');
        setError(null);
        setMessage(null);
        try {
            const versionId = await saveCurrentDraft();
            setMessage(`${planLabels[selectedPlanCode]} draft ${versionId} was saved.`);
        } catch (saveError: unknown) {
            setError(getSaasErrorMessage(saveError));
        } finally {
            setActionState('idle');
        }
    };

    const validate = async () => {
        setActionState('validating');
        setError(null);
        setMessage(null);
        try {
            if (verifyStripe) assertPublicationReady(currentDraft);
            const versionId = currentSaved ? currentDraft.versionId : await saveCurrentDraft();
            await validatePlanCatalogVersion(selectedPlanCode, versionId, verifyStripe);
            if (verifyStripe) {
                setStripeValidatedVersions((current) => ({ ...current, [selectedPlanCode]: versionId }));
            }
            setMessage(verifyStripe
                ? `Draft ${versionId} and all Stripe Prices are valid.`
                : `Draft ${versionId} is structurally valid. Stripe Prices were not checked.`);
        } catch (validationError: unknown) {
            setError(getSaasErrorMessage(
                validationError,
                'Catalogue validation failed unexpectedly. Nothing was published. Try again; if this continues, ' +
                'check the savePlanCatalogDraft and validatePlanCatalogVersion logs in Firebase.'
            ));
        } finally {
            setActionState('idle');
        }
    };

    const preview = async () => {
        setActionState('previewing');
        setError(null);
        setMessage(null);
        try {
            const versionId = currentSaved ? currentDraft.versionId : await saveCurrentDraft();
            const previewCatalog = await getPlanCatalogPreview({ [selectedPlanCode]: versionId });
            setPreviewPlans(previewCatalog.plans);
            setMessage(`Previewing ${planLabels[selectedPlanCode]} draft ${versionId} with the other published plans.`);
        } catch (previewError: unknown) {
            setError(getSaasErrorMessage(previewError));
        } finally {
            setActionState('idle');
        }
    };

    const publish = async () => {
        try {
            assertPublicationReady(currentDraft);
        } catch (publishReadinessError: unknown) {
            setError(getSaasErrorMessage(publishReadinessError));
            return;
        }
        if (!currentSaved || !currentStripeValidated) {
            setError('Save and validate this exact draft with Stripe verification before publishing.');
            return;
        }
        if (!window.confirm(
            `Publish ${planLabels[selectedPlanCode]} version ${currentDraft.versionId}? ` +
            'Published versions are immutable and new checkouts will use it immediately.'
        )) return;

        setActionState('publishing');
        setError(null);
        setMessage(null);
        try {
            await publishPlanCatalogVersion(selectedPlanCode, currentDraft.versionId);
            setPublishedVersionIds((current) => ({ ...current, [selectedPlanCode]: currentDraft.versionId }));
            setDrafts((current) => ({
                ...current,
                // A new immutable version must use fresh recurring Price IDs.
                [selectedPlanCode]: copyPublishedPlan(current[selectedPlanCode]),
            }));
            setSavedVersions((current) => ({ ...current, [selectedPlanCode]: undefined }));
            setStripeValidatedVersions((current) => ({ ...current, [selectedPlanCode]: undefined }));
            setPreviewPlans(null);
            setMessage(`${planLabels[selectedPlanCode]} version ${currentDraft.versionId} is now published.`);
        } catch (publishError: unknown) {
            setError(getSaasErrorMessage(publishError));
        } finally {
            setActionState('idle');
        }
    };

    const isBusy = actionState !== 'idle' || isUpdatingSpeechNeeds;
    const isEditingDisabled = isBusy || !catalogStateLoaded;
    const publishedVersionId = publishedVersionIds[selectedPlanCode];
    const currentPlanSpeechNeeds = planSpeechNeeds[selectedPlanCode];
    const currentSavedPlanSpeechNeeds = savedPlanSpeechNeeds[selectedPlanCode];
    const currentIncompatibleVersionIds = incompatibleVersionIds[selectedPlanCode] || [];
    const speechNeedsDirty = JSON.stringify(currentPlanSpeechNeeds) !== JSON.stringify(currentSavedPlanSpeechNeeds);
    const selectedSpeechNeedIds = new Set(currentPlanSpeechNeeds || []);
    const knownSpeechNeedIds = new Set(speechNeedOptions.map((speechNeed) => speechNeed.id));
    const configuredUnknownSpeechNeeds = (currentPlanSpeechNeeds || [])
        .filter((speechNeedId) => !knownSpeechNeedIds.has(speechNeedId))
        .map((speechNeedId) => ({ id: speechNeedId, name: 'Unknown or removed speech need' }));
    const displayedSpeechNeeds = [...speechNeedOptions, ...configuredUnknownSpeechNeeds];

    return (
        <section className='catalog-admin' aria-labelledby='catalog-admin-heading'>
            <header className='catalog-admin__header'>
                <div>
                    <span className='catalog-admin__root-badge'>Root only</span>
                    <h2 id='catalog-admin-heading'>SaaS plan catalogue</h2>
                    <p>Prepare and publish remotely configured plans. Checkout never trusts browser-supplied prices or Stripe identifiers.</p>
                </div>
                <button type='button' className='catalog-button catalog-button--secondary' onClick={() => loadAdminState(true)} disabled={isBusy}>
                    Reload catalogue drafts
                </button>
            </header>

            <div className='catalog-admin__tabs' role='tablist' aria-label='Plan drafts'>
                {planCodes.map((planCode) => (
                    <button
                        key={planCode}
                        type='button'
                        role='tab'
                        aria-selected={selectedPlanCode === planCode}
                        className={selectedPlanCode === planCode ? 'is-selected' : ''}
                        onClick={() => setSelectedPlanCode(planCode)}
                    >
                        {planLabels[planCode]}
                        {publishedVersionIds[planCode] && <small>Published: {publishedVersionIds[planCode]}</small>}
                    </button>
                ))}
            </div>

            <div className='catalog-admin__status-line'>
                <span>Editing draft</span>
                <strong>{currentDraft.versionId || 'New version'}</strong>
                <span>·</span>
                <span>Status</span>
                <strong>{currentSaved ? 'Saved draft' : 'Unsaved changes'}</strong>
                <span>·</span>
                <span>Current published</span>
                <strong>{publishedVersionId || 'None'}</strong>
            </div>

            {error && <div className='catalog-alert catalog-alert--error' role='alert'>{error}</div>}
            {message && <div className='catalog-alert' role='status'>{message}</div>}
            {currentIncompatibleVersionIds.length > 0 && (
                <div className='catalog-alert catalog-alert--warning' role='status'>
                    Preserved {currentIncompatibleVersionIds.length} incompatible legacy version
                    {currentIncompatibleVersionIds.length === 1 ? '' : 's'} for audit: {' '}
                    {currentIncompatibleVersionIds.join(', ')}. They were skipped so you can create and publish the new
                    {` ${planLabels[selectedPlanCode]} `}configuration.
                </div>
            )}

            <fieldset disabled={!catalogStateLoaded || isUpdatingSpeechNeeds}>
                <legend>Current tier speech-needs access</legend>
                <p className='catalog-help'>
                    This mutable allowlist is independent of commercial plan versions and Stripe Prices. Applying it updates every current self-service subscriber on this tier, as well as future subscribers. Only select completed speech needs that are safe to distribute.
                </p>
                {currentPlanSpeechNeeds === null && (
                    <div className='catalog-alert catalog-alert--warning' role='status'>
                        Unconfigured. Select at least one safe speech need before applying this tier allowlist.
                    </div>
                )}
                <div className='catalog-speech-needs__toolbar'>
                    <strong>{currentPlanSpeechNeeds === null ? 'Unconfigured' : `${currentPlanSpeechNeeds.length} selected`}</strong>
                    <button
                        type='button'
                        className='catalog-link-button'
                        onClick={() => {
                            setPlanSpeechNeeds((current) => ({ ...current, [selectedPlanCode]: [] }));
                            setMessage(null);
                            setError(null);
                        }}
                    >Clear selection</button>
                </div>
                {displayedSpeechNeeds.length === 0 ? (
                    <p className='catalog-help'>No speech needs are currently available in the SpeechNeeds collection.</p>
                ) : (
                    <div className='catalog-speech-needs' role='group' aria-label='Speech needs included in tier'>
                        {displayedSpeechNeeds.map((speechNeed) => (
                            <label key={speechNeed.id}>
                                <input
                                    type='checkbox'
                                    aria-label={`${speechNeed.name} (${speechNeed.id})`}
                                    checked={selectedSpeechNeedIds.has(speechNeed.id)}
                                    onChange={(event) => setSpeechNeed(speechNeed.id, event.target.checked)}
                                />
                                <span>{speechNeed.name}<small>{speechNeed.id}</small></span>
                            </label>
                        ))}
                    </div>
                )}
                <div className='catalog-speech-needs__apply'>
                    <button
                        type='button'
                        className='catalog-button'
                        onClick={applyPlanSpeechNeeds}
                        disabled={isUpdatingSpeechNeeds || !speechNeedsDirty || !currentPlanSpeechNeeds?.length}
                    >{isUpdatingSpeechNeeds ? 'Applying…' : 'Apply speech-needs access'}</button>
                    {!currentPlanSpeechNeeds?.length && <span>Select at least one speech need to apply.</span>}
                </div>
            </fieldset>

            <div className='catalog-admin__form'>
                <fieldset disabled={isEditingDisabled}>
                    <legend>Public presentation</legend>
                    <div className='catalog-grid catalog-grid--three'>
                        <label>Version ID<input value={currentDraft.versionId} onChange={(event) => updateDraft((draft) => { draft.versionId = event.target.value; })} placeholder='premium-20260825' /></label>
                        <label>Plan name<input value={currentDraft.name} onChange={(event) => updateDraft((draft) => { draft.name = event.target.value; })} /></label>
                        <label>Display order<input type='number' step='1' value={currentDraft.displayOrder} onChange={(event) => updateDraft((draft) => { draft.displayOrder = Number(event.target.value); })} /></label>
                    </div>
                    <label>Description<textarea rows={3} value={currentDraft.description} onChange={(event) => updateDraft((draft) => { draft.description = event.target.value; })} /></label>
                    <div className='catalog-check-row'>
                        <label><input type='checkbox' checked={currentDraft.publiclyPurchasable} onChange={(event) => updateDraft((draft) => { draft.publiclyPurchasable = event.target.checked; })} /> Publicly purchasable</label>
                        <label><input type='checkbox' checked={currentDraft.highlighted} onChange={(event) => updateDraft((draft) => { draft.highlighted = event.target.checked; })} /> Highlight as most popular</label>
                    </div>
                </fieldset>

                <fieldset disabled={isEditingDisabled}>
                    <legend>Prices and limits</legend>
                    <div className='catalog-grid catalog-grid--three'>
                        <label>Dashboard seats per unit<input type='number' min='1' step='1' value={currentDraft.dashboardSeatsPerUnit} onChange={(event) => updateDraft((draft) => { draft.dashboardSeatsPerUnit = Number(event.target.value); })} /></label>
                        <label>Family licences per unit/year<input type='number' min='1' step='1' value={currentDraft.familyLicensesPerUnit} onChange={(event) => updateDraft((draft) => { draft.familyLicensesPerUnit = Number(event.target.value); })} /></label>
                        <label>Maximum self-service units<input type='number' min='1' max='20' step='1' value={currentDraft.maximumSelfServeUnits} onChange={(event) => updateDraft((draft) => { draft.maximumSelfServeUnits = Number(event.target.value); })} /></label>
                    </div>
                    <div className='catalog-grid catalog-grid--two'>
                        <label>Monthly price per unit, excl. VAT (£)<input type='number' min='0' step='0.01' value={currentDraft.monthlyDisplayPrice ?? ''} onChange={(event) => updateDraft((draft) => { draft.monthlyDisplayPrice = numberOrUndefined(event.target.value); })} /></label>
                        <label>Annual price per unit, excl. VAT (£)<input type='number' min='0' step='0.01' value={currentDraft.annualDisplayPrice ?? ''} onChange={(event) => updateDraft((draft) => { draft.annualDisplayPrice = numberOrUndefined(event.target.value); })} /></label>
                        {selectedPlanCode !== 'starter' && <>
                            <label>Monthly Stripe Price ID<input value={currentDraft.monthlyStripePriceId || ''} onChange={(event) => updateDraft((draft) => { draft.monthlyStripePriceId = event.target.value || undefined; })} placeholder='price_…' /></label>
                            <label>Annual Stripe Price ID<input value={currentDraft.annualStripePriceId || ''} onChange={(event) => updateDraft((draft) => { draft.annualStripePriceId = event.target.value || undefined; })} placeholder='price_…' /></label>
                        </>}
                    </div>
                    <div className='catalog-grid catalog-grid--two'>
                        <label>Audio Scribe recording limit<input type='number' min='0' step='1' value={currentDraft.usageLimits.audioScribeRecordings ?? ''} placeholder='Unlimited' onChange={(event) => updateDraft((draft) => { draft.usageLimits.audioScribeRecordings = numberOrUndefined(event.target.value) ?? null; })} /></label>
                        <label>AI-generated report limit<input type='number' min='0' step='1' value={currentDraft.usageLimits.aiGeneratedReports ?? ''} placeholder='Unlimited' onChange={(event) => updateDraft((draft) => { draft.usageLimits.aiGeneratedReports = numberOrUndefined(event.target.value) ?? null; })} /></label>
                    </div>
                    {selectedPlanCode !== 'starter' && (
                        <p className='catalog-help'>Stripe Price IDs are per subscription unit and restricted configuration. Prices shown publicly exclude VAT; Stripe calculates applicable tax at checkout.</p>
                    )}
                </fieldset>

                <fieldset disabled={isEditingDisabled}>
                    <legend>Feature ceiling and role defaults</legend>
                    <p className='catalog-help'>The plan ceiling controls what roles may receive. Admins in the clinic can later switch purchased features off for a role, but cannot add anything outside this ceiling.</p>
                    <div className='catalog-feature-table' role='table' aria-label='Feature ceiling and default access'>
                        <div role='row' className='catalog-feature-table__header'>
                            <span role='columnheader'>Feature</span><span role='columnheader'>In tier</span><span role='columnheader'>Admin default</span><span role='columnheader'>Professional default</span>
                        </div>
                        {catalogFeatures.map((feature) => {
                            const included = currentDraft.allowedDashboardFeatures.includes(feature.id);
                            const core = coreFeatures.includes(feature.id);
                            const adminLocked = feature.id === 'organisation_team' || feature.id === 'organisation_team_manage';
                            const professionalLocked = feature.id === 'organisation_team_manage';
                            return (
                                <div role='row' className='catalog-feature-table__row' key={feature.id}>
                                    <span role='cell'><strong>{feature.label}</strong><small>{feature.help}</small></span>
                                    <span role='cell'><input aria-label={`${feature.label} included in tier`} type='checkbox' checked={included} disabled={core || isBusy} onChange={(event) => setAllowedFeature(feature.id, event.target.checked)} /></span>
                                    <span role='cell'><input aria-label={`${feature.label} Admin default`} type='checkbox' checked={currentDraft.defaultAccessByRole.admin?.[feature.id] === true} disabled={!included || adminLocked || isBusy} onChange={(event) => setRoleFeature('admin', feature.id, event.target.checked)} /></span>
                                    <span role='cell'><input aria-label={`${feature.label} Healthcare Professional default`} type='checkbox' checked={currentDraft.defaultAccessByRole.healthcare_professional?.[feature.id] === true} disabled={!included || professionalLocked || isBusy} onChange={(event) => setRoleFeature('healthcare_professional', feature.id, event.target.checked)} /></span>
                                </div>
                            );
                        })}
                    </div>
                </fieldset>

            </div>

            <div className='catalog-actions'>
                <button type='button' className='catalog-button' onClick={save} disabled={isEditingDisabled}>{actionState === 'saving' ? 'Saving…' : 'Save draft'}</button>
                <label className='catalog-verify'><input type='checkbox' checked={verifyStripe} onChange={(event) => setVerifyStripe(event.target.checked)} disabled={isEditingDisabled} /> Verify Stripe Prices</label>
                <button type='button' className='catalog-button catalog-button--secondary' onClick={validate} disabled={isEditingDisabled}>{actionState === 'validating' ? 'Validating…' : 'Validate'}</button>
                <button type='button' className='catalog-button catalog-button--secondary' onClick={preview} disabled={isEditingDisabled}>{actionState === 'previewing' ? 'Preparing preview…' : 'Preview'}</button>
                <button type='button' className='catalog-button catalog-button--publish' onClick={publish} disabled={isEditingDisabled || !currentSaved || !currentStripeValidated}>{actionState === 'publishing' ? 'Publishing…' : 'Publish version'}</button>
            </div>

            {previewPlans && (
                <section className='catalog-preview' aria-labelledby='catalog-preview-heading'>
                    <header><h3 id='catalog-preview-heading'>Private catalogue preview</h3><p>This preview includes the saved draft and is visible only in this Root session.</p></header>
                    <div className='catalog-preview__grid'>
                        {previewPlans.map((plan) => (
                            <article key={plan.versionId} className={plan.highlighted ? 'is-highlighted' : ''}>
                                {plan.highlighted && <span>Most popular</span>}
                                <h4>{plan.name}</h4>
                                <p>{plan.description}</p>
                                <strong>{formatGbp(plan.monthlyDisplayPrice)}<small>/month</small></strong>
                                <ul>
                                    <li>{plan.dashboardSeatsPerUnit} dashboard seat per unit</li>
                                    <li>{plan.familyLicensesPerUnit} family licences per unit/year</li>
                                    <li>{plan.allowedDashboardFeatures.length} enabled dashboard capabilities</li>
                                    <li>{plan.maximumSelfServeUnits} maximum self-service units</li>
                                </ul>
                                <small>Version {plan.versionId}</small>
                            </article>
                        ))}
                    </div>
                </section>
            )}
        </section>
    );
};
